/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from '../button/ButtonBase';
import useOutsideClick from '../hooks/useOutsideClick';
import { areEqualValues } from '../utils';
import useFormController from '../hooks/useFormController';
import useControlled from '../hooks/useControlled';
import formControllerState from '../utils/formControllerState';
import { ArrowDown } from '../private/icons/ArrowDown';
import useId from '../private/hooks/useId';
import { Menu } from '../menu/Menu';

const getNextNonDisabled = (arr, index, increment) => {
  if (!arr[index] || index < 0) {
    return null;
  }
  return arr[index].props?.disabled ? getNextNonDisabled(arr, index + increment, increment) : arr[index].props.value;
};

const Dropdown = (props) => {
  const {
    disabled,
    placeholder = 'Select an Option',
    children,
    value: valueProp,
    show: showProp,
    defaultValue: defaultValueProp,
    defaultOpen,
    onChange: onChangeProp,
    onClose,
    onOpen,
    maxItems = 5,
    menuProps,
    id,
    ...rest
  } = props;

  const [value, setValue] = useControlled({
    controlled: valueProp,
    defaultValue: defaultValueProp
  });
  const [show, setShow] = useControlled({
    controlled: showProp,
    defaultValue: defaultOpen
  });

  const childrenArray = React.Children.toArray(children);
  const selectedIndex = childrenArray.findIndex((child) => (areEqualValues(value, child.props.value)));
  const itemSelected = selectedIndex > -1;
  const selectedItem = itemSelected ? childrenArray[selectedIndex] : undefined;

  const [isMenuTopPlaced, setIsMenuTopPlaced] = useState(false);
  const [label, setLabel] = useState(selectedItem?.props?.children || null);
  const [descendantId, setDescendantId] = useState(null);

  const update = (event) => {
    if (show) {
      setShow(false);
      if (onClose) {
        onClose(event);
      }
    } else if (!show) {
      setShow(true);
      if (onOpen) {
        onOpen(event);
      }
    }
  };

  const onClickHandler = (event) => {
    update(event);
  };

  const wrapperRef = useRef(null);
  useOutsideClick(wrapperRef, useCallback(() => {
    setShow(false);
    if (onClose) {
      onClose();
    }
  }, [setShow, onClose]), show);

  const handleChange = (newValue, event) => {
    setValue(newValue);
    if (onChangeProp) {
      onChangeProp(newValue, event);
    }
  };

  const menuItemClickHandler = (newValue, event) => {
    update(event);
    if (value !== newValue) {
      handleChange(newValue, event);
    }
  };

  const keyDownHandler = (event) => {
    if (event.keyCode === 9) { // Tab key
      setShow(false);
      if (onClose) {
        onClose(event);
      }
    }
    if (event.keyCode === 13) { // Enter key
      update(event);
      event.preventDefault();
    }
    if (event.keyCode === 27 && show) { // Esc key
      // Stop event propagation if we use this event to collapse the menu list
      event.stopPropagation();
      update(event);
    }
    if (event.keyCode === 32) { // Space bar
      update(event);
      event.preventDefault(); // Avoid click event on button
    }
    if (event.keyCode === 40) { // Down key
      // Select next non-disabled item without hidding the menu list
      event.preventDefault();
      if (selectedIndex < childrenArray.length - 1) {
        let newValueIndex = selectedIndex + 1;
        const newValue = getNextNonDisabled(childrenArray, newValueIndex, 1);
        if (newValue !== null) {
          handleChange(newValue, event);
        }
      }
    } else if (event.keyCode === 38) { // Up key
      // Select previous non-disabled item without hidding the menu list
      event.preventDefault();
      if (selectedIndex > 0) {
        let newValueIndex = selectedIndex - 1;
        const newValue = getNextNonDisabled(childrenArray, newValueIndex, -1);
        if (newValue !== null) {
          handleChange(newValue, event);
        }
      }
    }
  };

  const formControl = useFormController();
  const fcs = formControllerState({
    props,
    formControl,
    states: ['disabled', 'required', 'status']
  });

  const onMenuPlacedHandler = (placement) => {
    setIsMenuTopPlaced(placement.startsWith('top'));
  };

  const buttonStylesMap = {
    base: 'sui-group sui-inline-flex sui-items-center sui-px-3 sui-py-2 sui-justify-between sui-font-regular sui-text-base sui-leading-tight sui-h-11 sui-w-full sui-rounded-base sui-border-solid sui-border-1',
    focus: 'focus-visible:sui-text-inverse focus-visible:sui-bg-inverse focus-visible:sui-outline focus-visible:sui-outline-4 focus-visible:sui-outline-focus'
  };

  const buttonClasses = classNames(`${buttonStylesMap.base} ${buttonStylesMap.focus}`,
    {
      'sui-bg-button-inactive-primary sui-border-primary sui-cursor-default': fcs.disabled,
      'sui-border-strong': !fcs.disabled,
      'hover:sui-border-hover': !fcs.disabled && !fcs.status,
      'sui-border-warning-strong': !fcs.disabled && fcs.status === 'warning',
      'sui-border-danger-strong': !fcs.disabled && fcs.status === 'error',
      'sui-border-success-strong': !fcs.disabled && fcs.status === 'success',
      'sui-rounded-t-none': show && isMenuTopPlaced,
      'sui-rounded-b-none': show && !isMenuTopPlaced,
      'sui-text-subtle': !itemSelected && !disabled,
      'sui-text-primary': itemSelected && !disabled,
      'sui-text-inactive': disabled
    },
  );

  const menuClasses = classNames('sui-overflow-y-auto', {
    'sui-rounded-t-base': isMenuTopPlaced,
    'sui-shadow-md sui-rounded-b-base': !isMenuTopPlaced,
  });

  const spanIconClasses = classNames('sui-flex sui-ml-4 sui-transition-transform sui-duration-250', {
    'group-focus-visible:sui-fill-inverse group-hover:sui-fill-brand': !fcs.disabled,
    'sui-fill-subtle': fcs.disabled,
    'sui-fill-medium': !show && !fcs.disabled,
    'sui-rotate-180 sui-fill-brand': show,
  });

  const childRefs = useMemo(() => ({}), []);
  const items = () => childrenArray.map((child) => {
    if (!React.isValidElement(child)) {
      return null;
    }
    const ref = React.createRef();
    childRefs[child.props.value] = ref;
    const selected = selectedItem === child;
    return React.cloneElement(child, {
      ref,
      selected,
      onClick: menuItemClickHandler
    });
  });

  useEffect(() => {
    if (selectedItem) {
      setLabel(selectedItem?.props?.children);
    }
  }, [selectedItem]);

  useEffect(() => {
    const selectedRef = childRefs[value];
    if (selectedItem && selectedRef && selectedRef.current !== null) {
      selectedRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'start'
      });
      setDescendantId(selectedRef.current.id);
    }
  }, [selectedItem, value, childRefs]);

  const menuId = useId(menuProps?.id);

  const [menuStyle, setMenuStyle] = useState({});
  const menuListRef = useCallback((domNode) => {
    if (domNode) {
      const domRect = domNode.getBoundingClientRect();
      const validChildren = React.Children.toArray(children).filter((child) => React.isValidElement(child)).length;
      const showScrollbar = validChildren > maxItems;
      const maxHeight = validChildren === 0 ? 0 : (domRect.height / validChildren) * maxItems;
      setMenuStyle(showScrollbar ? { maxHeight: `${maxHeight}px` } : {});
    }
  }, [children, maxItems]);

  const anchorRef = React.createRef();

  return (
    <div ref={wrapperRef} className="sui-relative sui-w-full">
      <ButtonBase
        ref={anchorRef}
        id={id}
        onClick={onClickHandler}
        onKeyDown={keyDownHandler}
        disabled={fcs.disabled}
        role="combobox"
        aria-disabled={fcs.disabled}
        aria-expanded={show}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-activedescendant={descendantId}
        className={buttonClasses}
        unstyled
        {...rest}
      >
        <span className="sui-text-ellipsis sui-overflow-hidden sui-whitespace-nowrap">
          {label || placeholder || <span>&nbsp;</span>}
        </span>
        <span className={spanIconClasses}>
          <ArrowDown size="small" color="inherit" />
        </span>
      </ButtonBase>
      <Menu
        open={show}
        anchorElementRef={anchorRef}
        className={menuClasses}
        onMenuPlaced={onMenuPlacedHandler}
        shadowed={!isMenuTopPlaced}
        matchAnchorWidth
        style={menuStyle}
        menuListProps={{ ref: menuListRef, id: menuId }}
      >
        {items()}
      </Menu>
    </div>
  );
};

Dropdown.displayName = 'Dropdown';

Dropdown.propTypes = {
  /**
   * A list of `MenuItem` elements to populate the dropdown with.
   */
  children: PropTypes.node,
  /**
   * Text to show when no value is selected.
   * @default 'Select an Option'
   */
  placeholder: PropTypes.string,
  /**
   * Event fired when the user closes the menu list.
   */
  onClose: PropTypes.func,
  /**
   * Event fired when the user opens the menu list.
   */
  onOpen: PropTypes.func,
  /**
   * Event fired when the user changes the selected list item.
   */
  onChange: PropTypes.func,
  /**
   * If `true`, the component is inactive
   */
  disabled: PropTypes.bool,
  /**
   * The value of the selected menu item.
   * Dropdown is considered controlled if, on initial render, value is not `undefined`.
   */
  value: PropTypes.node,
  /**
   * The id attribute that is added to the Dropdown element
   */
  id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  /**
   * The default value for the Dropdown element. Use when the component is not controlled.
   */
  defaultValue: PropTypes.node,
  /**
   * The default open state Dropdown element. Use when the component is not controlled.
   */
  defaultOpen: PropTypes.bool,
  /**
   * The maximum number of items to show before showing a scrollbar.
   * If there are more than maxItems, the maxHeight of the list will be calculated by averaging the MenuItem's height
   * @default 5
   */
  maxItems: PropTypes.number,
  /**
   * If `true`, the component is shown.
   */
  show: PropTypes.bool,
  /**
   * The prop values that will be added to the Menu component.
   */
  menuProps: PropTypes.object,
};

Dropdown.defaultProps = {};

export { Dropdown };